import React, { useState } from 'react';
import { Shield, Lock, AlertTriangle } from 'lucide-react';

const CORRECT_PASSWORD = 'NarrativeDominanceGame1234';

interface PasswordGateProps {
  onAuthenticated: () => void;
}

export const PasswordGate: React.FC<PasswordGateProps> = ({ onAuthenticated }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isShaking, setIsShaking] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password === CORRECT_PASSWORD) {
      sessionStorage.setItem('nd_authenticated', 'true');
      onAuthenticated();
    } else {
      setError('ACCESS DENIED - Invalid credentials');
      setPassword('');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950 flex items-center justify-center z-[9999]">
      {/* Background grid effect */}
      <div className="absolute inset-0 bg-tactical-grid opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/50 to-slate-950/90" />
      
      <div 
        className={`relative bg-slate-900/95 border-2 border-cyan-500/50 p-8 rounded-lg max-w-md w-full mx-4 shadow-2xl shadow-cyan-500/10 backdrop-blur ${
          isShaking ? 'animate-shake' : ''
        }`}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-cyan-500/10 rounded-full border border-cyan-500/30">
              <Shield size={48} className="text-cyan-400" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-cyan-400 tracking-wider mb-2">
            NARRATIVE DOMINANCE
          </h1>
          <p className="text-slate-400 text-sm tracking-wide">
            INFORMATION WARGAME // CLASSIFIED
          </p>
          <div className="mt-4 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2 tracking-wide flex items-center gap-2">
              <Lock size={14} />
              ENTER ACCESS CODE
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded text-white font-mono tracking-wider focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition-all placeholder:text-slate-600"
              placeholder="••••••••••••••••"
              autoFocus
            />
          </div>
          
          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/30 px-3 py-2 rounded">
              <AlertTriangle size={16} />
              {error}
            </div>
          )}
          
          <button
            type="submit"
            className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-3 px-4 rounded tracking-wider transition-all hover:shadow-lg hover:shadow-cyan-500/20 active:scale-[0.98]"
          >
            AUTHENTICATE
          </button>
        </form>
        
        <div className="mt-8 text-center">
          <p className="text-xs text-slate-600 tracking-wide">
            AUTHORIZED PERSONNEL ONLY
          </p>
          <p className="text-xs text-slate-700 mt-1">
            Unauthorized access attempts will be logged
          </p>
        </div>
      </div>

      {/* CSS for shake animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};
